import { useState, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/format';
import { FloatingActionButton } from '../../components/base/FloatingActionButton';
import { type PersonnelTailleur } from '../../mocks/personnel';
import { type TypeHabit } from '../../mocks/typesHabit';

interface UserInfo { id: string; nom: string; role: string; }
type Specialite = 'coupeur' | 'tailleur' | 'brodeur';

interface PersonnelFormModalProps {
  personnel: PersonnelTailleur | null;
  types: TypeHabit[];
  onClose: () => void;
  onSave: (data: PersonnelTailleur) => void;
}

function PersonnelFormModal({ personnel, types, onClose, onSave }: PersonnelFormModalProps) {
  const formId = useId();
  const isEdit = !!personnel;
  const [nom, setNom] = useState(personnel?.nom ?? '');
  const [telephone, setTelephone] = useState(personnel?.telephone ?? '');
  const [specialite, setSpecialite] = useState<Specialite>(personnel?.specialite ?? 'coupeur');
  const [actif, setActif] = useState(personnel?.actif ?? true);
  const [tarifsParType, setTarifsParType] = useState(() =>
    types.map(t => ({
      typeId: t.id,
      typeNom: t.nom,
      prix: personnel?.tarifsParType.find(tp => tp.typeId === t.id)?.prix ?? (specialite === 'coupeur' ? 3000 : 7000),
    }))
  );
  const [activeTypeTab, setActiveTypeTab] = useState<'global' | 'partype'>('partype');
  const [globalTarif, setGlobalTarif] = useState(personnel ? 
    (personnel.specialite === 'coupeur' ? personnel.tarifParCoupe : 
     personnel.specialite === 'tailleur' ? personnel.tarifParCouture : 
     (personnel as any).tarifParBroderie || 0) : 3000);

  const handleApplyGlobal = () => {
    setTarifsParType(prev => prev.map(t => ({ ...t, prix: globalTarif })));
  };

  const updateTarif = (typeId: string, prix: number) => {
    setTarifsParType(prev => prev.map(t => t.typeId === typeId ? { ...t, prix } : t));
  };

  const handleSave = () => {
    if (!nom.trim()) return;
    const id = personnel?.id ?? `pt${Date.now()}`;
    const tarifParCoupe = specialite === 'coupeur' ? (tarifsParType[0]?.prix ?? globalTarif) : 0;
    const tarifParCouture = specialite === 'tailleur' ? (tarifsParType[0]?.prix ?? globalTarif) : 0;
    onSave({
      id,
      nom,
      telephone,
      specialite,
      actif,
      tarifsParType,
      tarifParCoupe,
      tarifParCouture,
      tarifParBroderie: specialite === 'brodeur' ? (tarifsParType[0]?.prix ?? globalTarif) : 0,
      commandesTotal: personnel?.commandesTotal ?? 0,
      commandesMois: personnel?.commandesMois ?? 0,
      gainsMois: personnel?.gainsMois ?? 0,
    } as any);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-playfair font-bold text-lg text-gray-900">{isEdit ? `Modifier — ${personnel!.nom}` : 'Ajouter du personnel'}</h3>
          <button onClick={onClose} title="Fermer" className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-500">
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="px-6 py-5 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="col-span-2">
              <label htmlFor={`${formId}-nom`} className="block text-xs font-semibold text-gray-700 mb-1.5">Nom complet *</label>
              <input id={`${formId}-nom`} type="text" value={nom} onChange={e => setNom(e.target.value)} placeholder="Nom complet" title="Nom complet du personnel" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400" />
            </div>
            <div>
              <label htmlFor={`${formId}-tel`} className="block text-xs font-semibold text-gray-700 mb-1.5">Téléphone</label>
              <input id={`${formId}-tel`} type="text" value={telephone} onChange={e => setTelephone(e.target.value)} placeholder="+223 XX XX XX XX" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400" />
            </div>
            {!isEdit && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Spécialité</label>
                <div className="flex gap-1">
                  {(['coupeur', 'tailleur', 'brodeur'] as Specialite[]).map(s => (
                    <button key={s} onClick={() => setSpecialite(s)}
                      className={`flex-1 py-2.5 rounded-xl text-[10px] uppercase tracking-tighter font-black border cursor-pointer transition-colors whitespace-nowrap ${specialite === s ? 'bg-foret-500 text-white border-foret-500 shadow-md' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {s.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-800">Tarifs ({
                specialite === 'coupeur' ? 'coupe' : 
                specialite === 'tailleur' ? 'couture' : 'broderie'
              }) par type d&apos;habit</p>
              <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                {([['partype', 'Par type'], ['global', 'Global']] as const).map(([key, label]) => (
                  <button key={key} onClick={() => setActiveTypeTab(key)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${activeTypeTab === key ? 'bg-white text-gray-800' : 'text-gray-500'}`}
                  >{label}</button>
                ))}
              </div>
            </div>

            {activeTypeTab === 'global' ? (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-3">Définir un tarif unique appliqué à tous les types d&apos;habit</p>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <input
                      id="global-tarif-input"
                      type="number"
                      min={0}
                      value={globalTarif}
                      onChange={e => setGlobalTarif(Number(e.target.value))}
                      placeholder="Tarif unique"
                      title="Tarif global pour tous les types"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 pr-14"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
                  </div>
                  <button
                    onClick={handleApplyGlobal}
                    className="px-4 py-2.5 bg-foret-500 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-foret-600 whitespace-nowrap"
                  >
                    Appliquer à tous
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {tarifsParType.map((tarif) => {
                  const typeInfo = types.find(t => t.id === tarif.typeId);
                  return (
                    <div key={tarif.typeId} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                      <div className="w-8 h-8 bg-foret-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${typeInfo?.icon ?? 'ri-tshirt-line'} text-foret-600 text-sm`} />
                      </div>
                      <span className="flex-1 text-sm font-medium text-gray-700">{tarif.typeNom}</span>
                      <div className="relative w-36">
                        <input
                          id={`${formId}-tarif-${tarif.typeId}`}
                          type="number"
                          min={0}
                          value={tarif.prix}
                          onChange={e => updateTarif(tarif.typeId, Number(e.target.value))}
                          placeholder="Prix"
                          title={`Tarif pour ${tarif.typeNom}`}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-foret-400 pr-12"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl">
            <span className="text-sm font-medium text-gray-700">Actif</span>
            <button
              onClick={() => setActif(!actif)}
              title={actif ? "Désactiver" : "Activer"}
              className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${actif ? 'bg-foret-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${actif ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">Annuler</button>
          <button onClick={handleSave} disabled={!nom.trim()} className="flex-1 py-2.5 rounded-xl bg-foret-500 text-white font-semibold text-sm cursor-pointer hover:bg-foret-600 disabled:opacity-50 whitespace-nowrap">{isEdit ? 'Enregistrer' : 'Ajouter'}</button>
        </div>
      </div>
    </div>
  );
}

interface TypesHabitManagerProps {
  types: TypeHabit[];
  onClose: () => void;
  onSave: (types: TypeHabit[]) => void;
}

function TypesHabitManager({ types, onClose, onSave }: TypesHabitManagerProps) {
  const [localTypes, setLocalTypes] = useState([...types]);
  const [newNom, setNewNom] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editNom, setEditNom] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmed = newNom.trim();
    if (!trimmed) return;
    setLocalTypes(prev => [...prev, { id: `th_${Date.now()}`, nom: trimmed, icon: 'ri-tshirt-line', description: '' }]);
    setNewNom('');
  };

  const handleSaveEdit = (id: string) => {
    setLocalTypes(prev => prev.map(t => t.id === id ? { ...t, nom: editNom.trim() } : t));
    setEditId(null);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-playfair font-bold text-lg text-gray-900">Types d&apos;habit</h3>
          <button onClick={onClose} title="Fermer" className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 cursor-pointer text-gray-500">
            <i className="ri-close-line" />
          </button>
        </div>
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newNom}
              onChange={e => setNewNom(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="Nouveau type…"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400"
            />
            <button onClick={handleAdd} disabled={!newNom.trim()} className="px-4 py-2 bg-foret-500 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-foret-600 disabled:opacity-50 whitespace-nowrap">Ajouter</button>
          </div>
          <div className="space-y-2">
            {localTypes.map(t => (
              <div key={t.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5 group">
                <div className="w-7 h-7 bg-foret-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${t.icon} text-foret-600 text-sm`} />
                </div>
                  {editId === t.id ? (
                    <>
                      <input autoFocus id={`edit-type-${t.id}`} value={editNom} onChange={e => setEditNom(e.target.value)}
                        placeholder="Nom du type" title="Modifier le nom du type d'habit"
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(t.id); if (e.key === 'Escape') setEditId(null); }}
                        className="flex-1 px-2 py-1 text-sm border border-foret-300 rounded-lg bg-white focus:outline-none" />
                      <button onClick={() => handleSaveEdit(t.id)} title="Confirmer" className="text-xs px-2 py-0.5 bg-foret-500 text-white rounded-lg cursor-pointer whitespace-nowrap">OK</button>
                      <button onClick={() => setEditId(null)} title="Annuler" className="text-xs px-2 py-0.5 bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap">✕</button>
                    </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-gray-800">{t.nom}</span>
                    {deleteId === t.id ? (
                      <>
                        <span className="text-xs text-gray-500">Supprimer ?</span>
                        <button onClick={() => { setLocalTypes(prev => prev.filter(x => x.id !== t.id)); setDeleteId(null); }} className="text-xs px-2 py-0.5 bg-red-500 text-white rounded cursor-pointer whitespace-nowrap">Oui</button>
                        <button onClick={() => setDeleteId(null)} className="text-xs px-2 py-0.5 bg-gray-200 rounded cursor-pointer whitespace-nowrap">Non</button>
                      </>
                    ) : (
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setEditId(t.id); setEditNom(t.nom); }} className="w-6 h-6 flex items-center justify-center rounded-lg border border-gray-200 hover:border-foret-300 hover:text-foret-600 text-gray-400 cursor-pointer" title="Modifier">
                          <i className="ri-pencil-line text-xs" />
                        </button>
                        <button onClick={() => setDeleteId(t.id)} className="w-6 h-6 flex items-center justify-center rounded-lg border border-gray-200 hover:border-red-300 hover:text-red-500 text-gray-400 cursor-pointer" title="Supprimer">
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold cursor-pointer whitespace-nowrap">Annuler</button>
          <button onClick={() => { onSave(localTypes); onClose(); }} className="flex-1 py-2.5 rounded-xl bg-foret-500 text-white text-sm font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap">Confirmer</button>
        </div>
      </div>
    </div>
  );
}

function PersonnelHistoryModal({ personnel, onClose }: { personnel: PersonnelTailleur; onClose: () => void }) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
      api.getPersonnelHistory(token, personnel.id)
        .then(setHistory)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [personnel.id]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[2.5rem] p-10 w-full max-w-3xl shadow-2xl flex flex-col max-h-[85vh] transform animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-foret-50 rounded-3xl flex items-center justify-center">
              <i className="ri-history-line text-3xl text-foret-600" />
            </div>
            <div>
              <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight">Historique — {personnel.nom}</h3>
              <p className="text-gray-400 font-medium mt-1">Dernières commandes assignées</p>
            </div>
          </div>
          <button onClick={onClose} title="Fermer" className="w-12 h-12 flex items-center justify-center rounded-2xl hover:bg-gray-100 text-gray-400 cursor-pointer transition-all active:scale-95">
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {loading ? (
            <div className="py-24 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foret-600 mx-auto"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="py-24 text-center text-gray-400 font-bold">Aucune commande trouvée</div>
          ) : (
            <div className="bg-gray-50 rounded-[2rem] overflow-hidden border border-gray-100">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-100/50">
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-gray-400 tracking-widest">Commande</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-gray-400 tracking-widest">Client / Modèle</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-gray-400 tracking-widest">Tâche</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase text-gray-400 tracking-widest">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((h, i) => (
                    <tr key={i} className="hover:bg-white transition-colors">
                      <td className="px-6 py-4 font-black text-gray-900 text-sm">#{h.commande_code || h.commande_id}</td>
                      <td className="px-6 py-4">
                        <p className="text-xs font-bold text-gray-800">{h.client_nom}</p>
                        <p className="text-[10px] text-gray-400 italic">{h.modele_nom}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[10px] font-black uppercase tracking-tighter text-foret-700 bg-foret-50 px-2 py-1 rounded-lg">
                          {h.type === 'coupeur' ? 'Coupe' : h.type === 'brodeur' ? 'Broderie' : 'Couture'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                         <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                           h.statut === 'TERMINE' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'
                         }`}>
                           {h.statut}
                         </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ParametresPersonnel() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [personnel, setPersonnel] = useState<PersonnelTailleur[]>([]);
  const [trashPersonnel, setTrashPersonnel] = useState<PersonnelTailleur[]>([]);
  const [showTrash, setShowTrash] = useState(false);
  const [types, setTypes] = useState<TypeHabit[]>([]);
  const [activeTab, setActiveTab] = useState<Specialite | 'tous'>('tous');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState<PersonnelTailleur | null>(null);
  const [showTypesManager, setShowTypesManager] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showTarifsId, setShowTarifsId] = useState<string | null>(null);
  const [showHistoryPersonnel, setShowHistoryPersonnel] = useState<PersonnelTailleur | null>(null);
  const [loading, setLoading] = useState(true);

  const roleLabels: Record<string, string> = {
    'coupeur': 'Coupeur',
    'tailleur': 'Tailleur',
    'brodeur': 'Brodeur'
  };

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      fetchData(token);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchData = async (token: string) => {
    try {
      const [persData, typesData] = await Promise.all([
        api.getPersonnel(token),
        api.getHabitTypes(token)
      ]);
      setTypes(typesData);
      setPersonnel(persData.map((p: any) => ({
          ...p,
          id: String(p.id),
          tarifsParType: Array.isArray(p.tarifs_json) ? p.tarifs_json : [],
          tarifParCoupe: Number(p.tarif_par_coupe || 0),
          tarifParCouture: Number(p.tarif_par_couture || 0),
          tarifParBroderie: Number(p.tarif_par_broderie || 0),
          commandesMois: Number(p.commandes_mois || 0),
          commandesTotal: Number(p.commandes_total || 0),
          gainsMois: Number(p.gains_mois || 0),
          actif: !!p.actif
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrash = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const data = await api.getTrashPersonnel(token);
      setTrashPersonnel(data);
    } catch (err) {
      console.error(err);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleRestore = async (id: string | number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.restorePersonnel(token, Number(id));
      showToast("Personnel restauré");
      fetchData(token);
      fetchTrash();
    } catch (err) {
      showToast("Erreur lors de la restauration", "error");
    }
  };

  const handleHardDelete = async (id: string | number) => {
    if (!window.confirm("Action irréversible. Supprimer définitivement ce personnel ?")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.hardDeletePersonnel(token, Number(id));
      showToast("Personnel supprimé définitivement");
      fetchTrash();
    } catch (err) {
      showToast("Erreur lors de la suppression définitive", "error");
    }
  };

  const filtered = personnel.filter(p => activeTab === 'tous' || p.specialite === activeTab);

  const openAdd = () => { setSelectedPersonnel(null); setShowEditModal(true); };
  const openEdit = (p: PersonnelTailleur) => { setSelectedPersonnel(p); setShowEditModal(true); };

  const handleSave = async (data: PersonnelTailleur) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      const payload = {
        nom: data.nom,
        telephone: data.telephone,
        specialite: data.specialite,
        actif: data.actif ? 1 : 0,
        tarif_par_coupe: data.specialite === 'coupeur' ? (data.tarifsParType[0]?.prix || 0) : 0,
        tarif_par_couture: data.specialite === 'tailleur' ? (data.tarifsParType[0]?.prix || 0) : 0,
        tarif_par_broderie: data.specialite === 'brodeur' ? (data.tarifsParType[0]?.prix || 0) : 0,
        tarifs_json: data.tarifsParType
      };

      if (selectedPersonnel) {
        await api.updatePersonnel(token, Number(data.id), payload);
        showToast(`${data.nom} mis à jour`);
      } else {
        await api.createPersonnel(token, payload);
        showToast(`${data.nom} ajouté`);
      }
      fetchData(token);
      setShowEditModal(false);
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde';
      showToast(message, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.softDeletePersonnel(token, Number(id));
      setPersonnel(prev => prev.filter(p => p.id !== id));
      setConfirmDeleteId(null);
      showToast(`Personnel retiré`);
    } catch (err) {
      console.error(err);
       showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleToggleActif = async (id: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    const p = personnel.find(p => p.id === id);
    if (!p) return;
    try {
      await api.updatePersonnel(token, Number(id), { ...p, actif: !p.actif ? 1 : 0 });
      setPersonnel(prev => prev.map(pers => pers.id === id ? { ...pers, actif: !pers.actif } : pers));
      showToast(`${p.nom} ${p.actif ? 'désactivé' : 'activé'}`);
    } catch (err) {
      console.error(err);
    }
  };

  const totalGainsMois = personnel.filter(p => p.actif).reduce((s, p) => s + p.gainsMois, 0);
  const totalCmdsMois = personnel.filter(p => p.actif).reduce((s, p) => s + p.commandesMois, 0);

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Configuration du personnel</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {personnel.filter(p => p.actif).length} actifs · {totalCmdsMois} commandes ce mois ·{' '}
              {formatPrice(totalGainsMois)} versés
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { fetchTrash(); setShowTrash(true); }}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors"
              title="Corbeille"
            >
              <i className="ri-delete-bin-line" />
              <span className="hidden sm:inline whitespace-nowrap">Corbeille</span>
            </button>
            <button
              onClick={() => setShowTypesManager(true)}
              className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors"
              title="Types d'habit"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-shirt-line" /></div>
              <span className="hidden sm:inline whitespace-nowrap">Types d&apos;habit ({types.length})</span>
            </button>
            <button
              onClick={openAdd}
              className="hidden md:flex items-center gap-2 bg-foret-500 hover:bg-foret-600 text-white px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line" /></div>
              Ajouter personnel
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Coupeurs actifs', value: personnel.filter(p => p.specialite === 'coupeur' && p.actif).length, icon: 'ri-scissors-line', color: 'bg-terre-50 border-terre-200 text-terre-700' },
              { label: 'Tailleurs actifs', value: personnel.filter(p => p.specialite === 'tailleur' && p.actif).length, icon: 'ri-sewing-thread-line', color: 'bg-foret-50 border-foret-200 text-foret-700' },
              { label: 'Brodeurs actifs', value: personnel.filter(p => p.specialite === 'brodeur' && p.actif).length, icon: 'ri-magic-line', color: 'bg-amber-50 border-amber-200 text-amber-700' },
              { label: 'Commandes / mois', value: totalCmdsMois, icon: 'ri-file-list-3-line', color: 'bg-or-50 border-or-200 text-or-700' },
              { label: 'Total versé / mois', value: formatPrice(totalGainsMois), icon: 'ri-money-franc-circle-line', color: 'bg-green-50 border-green-200 text-green-700' },
            ].map(s => (
              <div key={s.label} className={`rounded-xl border px-4 py-3 ${s.color}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 flex items-center justify-center"><i className={s.icon} /></div>
                  <span className="text-xs font-medium opacity-80">{s.label}</span>
                </div>
                <p className="text-xl font-bold">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 w-fit mb-5">
            {([['tous', 'Tous'], ['coupeur', 'Coupeurs'], ['tailleur', 'Tailleurs'], ['brodeur', 'Brodeurs']] as const).map(([key, label]) => (
              <button key={key} onClick={() => setActiveTab(key as any)}
                className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === key ? 'bg-foret-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >{label}</button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(p => (
              <div key={p.id} className={`bg-white rounded-2xl border p-5 transition-all ${!p.actif ? 'opacity-60 border-gray-100' : 'border-gray-100'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${p.specialite === 'coupeur' ? 'bg-terre-400' : 'bg-foret-400'}`}>
                      {p.nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 leading-tight">{p.nom}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase tracking-tighter ${
                        p.specialite === 'coupeur' ? 'bg-terre-100 text-terre-700' : 
                        p.specialite === 'tailleur' ? 'bg-foret-100 text-foret-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {roleLabels[p.specialite] || p.specialite}
                      </span>
                    </div>
                  </div>
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${p.actif ? 'bg-green-400' : 'bg-gray-300'}`} />
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-phone-line" /></div>
                    <span>{p.telephone || '—'}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <button
                    onClick={() => setShowTarifsId(showTarifsId === p.id ? null : p.id)}
                    className="flex items-center justify-between w-full bg-gray-50 rounded-xl px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors group"
                  >
                    <span className="text-xs font-semibold text-gray-600">Tarifs par type d&apos;habit</span>
                    <i className={`ri-arrow-${showTarifsId === p.id ? 'up' : 'down'}-s-line text-gray-400 text-sm`} />
                  </button>
                  {showTarifsId === p.id && (
                    <div className="mt-2 space-y-1 max-h-36 overflow-y-auto">
                      {p.tarifsParType.map(t => (
                        <div key={t.typeId} className="flex items-center justify-between text-xs px-3 py-1.5 bg-gray-50 rounded-lg">
                          <span className="text-gray-600">{t.typeNom}</span>
                          <span className="font-semibold text-foret-600">{formatPrice(t.prix)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Ce mois</p>
                    <p className="text-sm font-bold text-gray-800">{p.commandesMois} cmd</p>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Gains</p>
                    <p className="text-sm font-bold text-foret-600">{formatPrice(p.gainsMois)}</p>
                  </div>
                  <div className="w-px h-6 bg-gray-200" />
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Total</p>
                    <p className="text-sm font-bold text-gray-800">{p.commandesTotal}</p>
                  </div>
                </div>

                  <div className="flex gap-2">
                    <button 
                      onClick={() => setShowHistoryPersonnel(p)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-foret-500 hover:border-foret-200 cursor-pointer transition-colors"
                      title="Voir l'historique"
                    >
                      <i className="ri-history-line text-xs" />
                    </button>
                    <button onClick={() => openEdit(p)} className="flex-1 text-xs py-2 rounded-lg border border-foret-200 text-foret-600 hover:bg-foret-50 cursor-pointer font-semibold transition-colors whitespace-nowrap">
                      <i className="ri-edit-line mr-1" />Modifier
                    </button>
                    <button onClick={() => handleToggleActif(p.id)} className={`flex-1 text-xs py-2 rounded-lg border cursor-pointer font-semibold transition-colors whitespace-nowrap ${p.actif ? 'border-red-200 text-red-500 hover:bg-red-50' : 'border-green-200 text-green-500 hover:bg-green-50'}`}>
                      {p.actif ? 'Désactiver' : 'Activer'}
                    </button>
                  <button onClick={() => setConfirmDeleteId(p.id)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 cursor-pointer transition-colors" title="Mettre à la corbeille">
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>

      {showEditModal && (
        <PersonnelFormModal
          personnel={selectedPersonnel}
          types={types}
          onClose={() => setShowEditModal(false)}
          onSave={handleSave}
        />
      )}

      <FloatingActionButton 
        onClick={openAdd} 
        icon="ri-user-add-line" 
        label="Ajouter personnel" 
      />

      {showTypesManager && (
        <TypesHabitManager
          types={types}
          onClose={() => setShowTypesManager(false)}
          onSave={async (newTypes) => {
              const token = localStorage.getItem('atelier_bassoum_token');
              if (!token) return;
              try {
                  const added = newTypes.filter(nt => !types.some(t => t.id === nt.id));
                  const deleted = types.filter(t => !newTypes.some(nt => nt.id === t.id));
                  const updated = newTypes.filter(nt => {
                      const orig = types.find(t => t.id === nt.id);
                      return orig && orig.nom !== nt.nom;
                  });

                  const promises: Promise<any>[] = [];

                  added.forEach(t => {
                      promises.push(api.saveHabitType(token, { nom: t.nom, prix: parseFloat(String((t as any).prix)) || 0 }));
                  });

                  updated.forEach(t => {
                      promises.push(api.saveHabitType(token, { id: Number(t.id), nom: t.nom, prix: parseFloat(String((t as any).prix)) || 0 }));
                  });

                  deleted.forEach(t => {
                      promises.push(api.deleteHabitType(token, Number(t.id)));
                  });

                  await Promise.all(promises);
                  showToast("Types d'habit mis à jour avec succès");
                  fetchData(token);
              } catch (err) {
                  console.error(err);
                  showToast("Erreur lors de la mise à jour des types d'habit", "error");
              }
          }}
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="font-playfair font-bold text-lg text-gray-900 mb-2">Retirer ce membre ?</h3>
            <p className="text-sm text-gray-500 mb-6">Cette action ne peut pas être annulée.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-semibold cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={() => handleDelete(confirmDeleteId)} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {showTrash && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setShowTrash(false)}>
          <div className="bg-white rounded-[2.5rem] p-10 w-full max-w-2xl shadow-2xl flex flex-col max-h-[85vh] transform animate-in slide-in-from-bottom-10" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-red-50 rounded-3xl flex items-center justify-center">
                    <i className="ri-delete-bin-7-line text-3xl text-red-500" />
                </div>
                <div>
                    <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight">Corbeille Personnel</h3>
                    <p className="text-gray-400 font-medium mt-1">Gestion des employés supprimés</p>
                </div>
              </div>
              <button onClick={() => setShowTrash(false)} title="Fermer" className="w-12 h-12 flex items-center justify-center rounded-2xl hover:bg-gray-100 text-gray-400 cursor-pointer transition-all active:scale-95">
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              {trashPersonnel.length === 0 ? (
                <div className="py-24 text-center">
                    <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="ri-inbox-line text-5xl text-gray-200" />
                    </div>
                    <p className="text-gray-400 font-bold text-lg font-playfair">La corbeille est vide</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {trashPersonnel.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-5 rounded-[2rem] border border-gray-100 hover:border-red-100 hover:bg-red-50/10 transition-all">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center text-white text-lg font-black shrink-0">
                          {p.nom.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-black text-gray-900 leading-tight">{p.nom}</p>
                          <p className="text-xs text-gray-400 font-bold mt-0.5">{roleLabels[p.specialite] || p.specialite}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                            onClick={() => handleRestore(p.id)}
                            className="px-5 py-2.5 bg-foret-500 text-white rounded-xl text-xs font-black hover:bg-foret-600 transition-all cursor-pointer shadow-lg shadow-foret-100"
                            title="Restaurer ce personnel"
                        >
                            Restaurer
                        </button>
                        <button 
                            onClick={() => handleHardDelete(p.id)}
                            className="w-10 h-10 bg-red-50 text-red-500 rounded-xl flex items-center justify-center hover:bg-red-500 hover:text-white transition-all cursor-pointer"
                            title="Supprimer définitivement"
                        >
                            <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="pt-8 border-t border-gray-100 mt-8 flex justify-end">
                <button 
                    onClick={() => setShowTrash(false)}
                    className="px-10 py-4 bg-gray-900 text-white rounded-2xl text-sm font-black hover:bg-black transition-all cursor-pointer shadow-xl active:scale-95"
                >
                    Fermer
                </button>
            </div>
          </div>
        </div>
      )}

      {showHistoryPersonnel && (
        <PersonnelHistoryModal 
          personnel={showHistoryPersonnel}
          onClose={() => setShowHistoryPersonnel(null)}
        />
      )}
    </div>
  );
}
